"use client";

import { c as _c } from "react/compiler-runtime";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { PropColumn, PropSchemaSummary } from "@/stores/templatesStore";
import { IconAlertInfoOutline18, IconAlertWarningOutline18, IconClipboardArrowInOutline18, IconPlusOutline18, IconTrash2Outline18 } from "nucleo-ui-outline-18";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { buildCellContext, DEFAULT_CHAIN_PROP_KEY, getCellSpec, stripEmptyRows, validateCsvTable, type CellContext, type CellSpec, type TokenInfo } from "./csvTableCells";

/**
 * Storage shape for a csvTable prop value.
 * Object-of-columnar-arrays keeps payload templates simple:
 *   {{$props.<propKey>.<colKey>}} resolves to the column's array.
 */
export type CsvTableValue = Record<string, Array<string | number>>;

/**
 * Cap for the paste textarea, in characters. ~200KB covers ~4000 rows of
 * `0x<40-hex>,<atomic-amount>` and prevents pathological pastes from
 * freezing the tab before our row-level maxRows cap runs.
 */
const PASTE_MAX_CHARS = 200_000;

/**
 * Max rows rendered in the grid. Beyond this, we show a banner and only
 * paint the first N rows — the full data is still held in state and submitted.
 * Protects against thousand-row pastes mounting thousands of AddressInputs
 * (with wallet-picker popovers, etc.) and freezing the tab.
 */
const DISPLAY_MAX_ROWS = 500;
interface CsvTableInputProps {
  columns: PropColumn[];
  value: CsvTableValue | undefined;
  onChange: (value: CsvTableValue) => void;
  minRows?: number;
  maxRows?: number;
  /** Other prop values (for cross-column references like chainPropKey). */
  propValues?: Record<string, unknown>;
  /** Optional footer summary config (aggregates rendered below the grid). */
  summary?: PropSchemaSummary;
  /**
   * Token metadata resolved per tokenAmount column, provided by the parent
   * (`ConnectorInputModal`) which owns the `useSimTokensInfo` call. Feeds the
   * cell's helper line (`= <atomic> atomic`) and the sum-aggregate formatter.
   * Optional — cells degrade gracefully when absent or still loading.
   */
  tokenInfoByColKey?: Record<string, TokenInfo | undefined>;
}
type RowCells = Record<string, string | number>;

/** Internal edit model. `__rid` is a stable React key so row removal preserves focus on sibling cells. */
interface RowModel {
  __rid: string;
  cells: RowCells;
}
let nextRid = 0;
function makeRid(): string {
  nextRid += 1;
  return `r${nextRid}`;
}
function emptyCells(columns: PropColumn[]): RowCells {
  const cells: RowCells = {};
  for (const c of columns) cells[c.key] = "";
  return cells;
}

/** Convert column-wise storage → row-wise edit model, minting new __rids. */
function toRows(value: CsvTableValue | undefined, columns: PropColumn[]): RowModel[] {
  const rowCount = Math.max(0, ...columns.map(c => Array.isArray(value?.[c.key]) ? value![c.key].length : 0));
  const rows: RowModel[] = [];
  for (let i = 0; i < rowCount; i++) {
    const cells: RowCells = {};
    for (const c of columns) cells[c.key] = value?.[c.key]?.[i] ?? "";
    rows.push({
      __rid: makeRid(),
      cells
    });
  }
  return rows;
}

/** Convert row-wise edit model → column-wise storage (drops __rid). */
function toColumns(rows: RowModel[], columns: PropColumn[]): CsvTableValue {
  const out: CsvTableValue = {};
  for (const c of columns) out[c.key] = rows.map(r => r.cells[c.key] ?? "");
  return out;
}

/** Deep-equal check on the columnar storage shape. Keeps the value→rows effect
 *  quiet when the parent returns the same content in a fresh reference. */
function columnarEqual(a: CsvTableValue | undefined, b: CsvTableValue | undefined, columns: PropColumn[]): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  for (const c of columns) {
    const av = a[c.key] ?? [];
    const bv = b[c.key] ?? [];
    if (av.length !== bv.length) return false;
    for (let i = 0; i < av.length; i++) {
      if (av[i] !== bv[i]) return false;
    }
  }
  return true;
}
interface ParsedCsv {
  /** Rows truncated/padded to match `columns.length`. */
  rows: string[][];
  /** Max token count observed on any source row — lets the caller warn when the paste had more columns than the grid accepts. */
  maxColsSeen: number;
}

/** Parse a CSV/TSV blob into rows of raw cell strings, honoring column order.
 *  Exported for unit tests; prefer the component for production callers. */
export function parseCsv(blob: string, columns: PropColumn[]): ParsedCsv {
  const lines = blob.split(/\r?\n/).map(l => l.trim()).filter(l => l.length > 0);
  let maxColsSeen = 0;
  const rows = lines.map(line => {
    const cells = line.includes("\t") ? line.split("\t") : line.split(",");
    if (cells.length > maxColsSeen) maxColsSeen = cells.length;
    return columns.map((_, i) => (cells[i] ?? "").trim());
  });
  return {
    rows,
    maxColsSeen
  };
}

/** Apply the registry's coerce hook for a column, with a safe default. */
function coerceViaRegistry(raw: string, column: PropColumn, spec: CellSpec): string | number {
  if (raw === "") return "";
  return spec.coerce ? spec.coerce(raw, column) : raw;
}

/**
 * Grid editor for parallel-array template props. Users see paired rows; the
 * stored value is columnar so payload expressions (`{{$props.x.colKey}}`)
 * stay simple and the expression engine needs no changes.
 *
 * Per-cell rendering, validation, and coercion are delegated to the cell
 * registry in `csvTableCells.tsx`. Adding a new inputType for columns means
 * registering a CellSpec there — this layout component never needs to change.
 */
export default function CsvTableInput({
  columns,
  value,
  onChange,
  minRows = 1,
  maxRows,
  propValues,
  summary,
  tokenInfoByColKey
}: CsvTableInputProps) {
  // Rows are internal state (not derived from value each render) so row
  // __rids stay stable across re-renders — critical for React.memo on rows
  // and for focus preservation when the user deletes an adjacent row.
  const [rows, setRows] = useState<RowModel[]>(() => {
    const seeded = toRows(value, columns);
    while (seeded.length < Math.max(1, minRows)) {
      seeded.push({
        __rid: makeRid(),
        cells: emptyCells(columns)
      });
    }
    return seeded;
  });

  // Every write path below assigns lastSeenValueRef.current to the exact
  // tightened columnar we hand to onChange, so the parent returning the same
  // reference back short-circuits here. For the rarer "external reset" case
  // (modal close, template swap), we reseed. `rows` intentionally stays out
  // of the deps — it changes on every keystroke but tells us nothing new.
  const lastSeenValueRef = useRef<CsvTableValue | undefined>(value);
  useEffect(() => {
    if (value === lastSeenValueRef.current) return;
    if (columnarEqual(value, lastSeenValueRef.current, columns)) {
      lastSeenValueRef.current = value;
      return;
    }
    const seeded = toRows(value, columns);
    while (seeded.length < Math.max(1, minRows)) {
      seeded.push({
        __rid: makeRid(),
        cells: emptyCells(columns)
      });
    }
    lastSeenValueRef.current = value;
    setRows(seeded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, columns, minRows]);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");
  /** Post-paste toast-style warning shown inline (dropped columns, maxRows truncation). */
  const [pasteNotice, setPasteNotice] = useState<string | null>(null);

  // Key the CellContext on the primitives it actually consumes: the set of
  // sibling prop values the cell registry reads via `chainPropKey`. Without
  // this, any unrelated prop change (token address, notification channel,
  // etc.) allocates a new ctx and re-runs the full validator walk over
  // every cell on every keystroke anywhere in the modal.
  const chainPropDepKey = useMemo(() => {
    const keys = Array.from(new Set(columns.map(c => c.chainPropKey ?? DEFAULT_CHAIN_PROP_KEY))).sort();
    return keys.map(k => `${k}=${String(propValues?.[k] ?? "")}`).join("|");
  }, [columns, propValues]);

  // Narrow dep key for CellContext memo: include token-info so validators can
  // enforce per-token decimal bounds and the sum aggregate can format.
  const tokenInfoDepKey = useMemo(() => {
    if (!tokenInfoByColKey) return "";
    return Object.entries(tokenInfoByColKey).map(([k, v]) => `${k}=${v?.decimals ?? ""}:${v?.symbol ?? ""}`).sort().join("|");
  }, [tokenInfoByColKey]);
  const ctx: CellContext = useMemo(() => buildCellContext(propValues, tokenInfoByColKey),
  // propValues identity may churn; key on the narrower slice we actually read.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [chainPropDepKey, tokenInfoDepKey]);
  const specs = useMemo(() => columns.map(c => getCellSpec(c)), [columns]);

  // Read rows via ref inside handlers so callback identities stay stable
  // across unrelated keystrokes (rows changes every edit; including it in
  // the useCallback deps would defeat the memoized CsvRow children). Handlers
  // compute the next rows outside of setRows — React 19's StrictMode double-
  // invokes updater functions in dev, so any side effect (onChange) inside
  // setRows would fire twice per edit.
  const rowsRef = useRef(rows);
  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);
  const emit = useCallback((next: RowModel[], justEmptied: boolean) => {
    const columnar = toColumns(next, columns);
    const tightened = justEmptied ? stripEmptyRows(columnar, columns) as CsvTableValue : columnar;
    lastSeenValueRef.current = tightened;
    setRows(next);
    onChange(tightened);
  }, [columns, onChange]);
  const setCell = useCallback((rid: string, colKey: string, raw: string) => {
    const cur = rowsRef.current;
    const idx = cur.findIndex(r => r.__rid === rid);
    if (idx === -1) return;
    const colIndex = columns.findIndex(c => c.key === colKey);
    if (colIndex === -1) return;
    const col = columns[colIndex];
    const coerced = coerceViaRegistry(raw, col, specs[colIndex]);
    if (cur[idx].cells[colKey] === coerced) return; // no-op
    const nextRow: RowModel = {
      __rid: rid,
      cells: {
        ...cur[idx].cells,
        [colKey]: coerced
      }
    };
    const next = cur.slice();
    next[idx] = nextRow;
    // Only run stripEmptyRows when the edit could have emptied a row —
    // typing a non-empty character into a non-empty row can't.
    emit(next, coerced === "");
  }, [columns, specs, emit]);
  const addRow = useCallback(() => {
    const cur = rowsRef.current;
    if (maxRows && cur.length >= maxRows) return;
    emit([...cur, {
      __rid: makeRid(),
      cells: emptyCells(columns)
    }], true);
  }, [maxRows, columns, emit]);
  const removeRow = useCallback((rid: string) => {
    const cur = rowsRef.current;
    if (cur.length <= Math.max(1, minRows)) return;
    emit(cur.filter(r => r.__rid !== rid), true);
  }, [minRows, emit]);
  const applyPaste = () => {
    const {
      rows: parsed,
      maxColsSeen
    } = parseCsv(pasteText, columns);
    if (parsed.length === 0) {
      setPasteNotice(null);
      setPasteOpen(false);
      return;
    }
    const coerced: RowModel[] = parsed.map(cellsRow => {
      const cells: RowCells = {};
      columns.forEach((c, i) => {
        cells[c.key] = coerceViaRegistry(cellsRow[i] ?? "", c, specs[i]);
      });
      return {
        __rid: makeRid(),
        cells
      };
    });
    const truncated = maxRows && coerced.length > maxRows;
    const capped = truncated ? coerced.slice(0, maxRows) : coerced;
    const notices: string[] = [];
    if (maxColsSeen > columns.length) {
      notices.push(`${maxColsSeen - columns.length} extra column(s) in your paste were dropped — only the first ${columns.length} are imported.`);
    }
    if (truncated) {
      notices.push(`Only the first ${maxRows} of ${coerced.length} pasted rows were kept (maxRows limit).`);
    }
    setPasteNotice(notices.length ? notices.join(" ") : null);
    emit(capped, true);
    setPasteText("");
    setPasteOpen(false);
  };
  const canRemove = rows.length > Math.max(1, minRows);
  const isTruncated = rows.length > DISPLAY_MAX_ROWS;
  // Disable Add in truncated mode — an added row would land past the display
  // cutoff (invisible), which is confusing. User must clear hidden rows first.
  const canAdd = (!maxRows || rows.length < maxRows) && !isTruncated;
  const clearHiddenRows = useCallback(() => {
    const cur = rowsRef.current;
    if (cur.length <= DISPLAY_MAX_ROWS) return;
    emit(cur.slice(0, DISPLAY_MAX_ROWS), true);
  }, [emit]);

  // Reuse the shared validator so grid-side cell errors and modal-side
  // submit gate can never disagree. Validate on the current rows state
  // (local truth) — the parent value is always kept in sync via commit.
  const columnarFromRows = useMemo(() => toColumns(rows, columns), [rows, columns]);
  const {
    cellErrors
  } = useMemo(() => validateCsvTable(columnarFromRows, columns, ctx, {
    minRows
  }), [columnarFromRows, columns, ctx, minRows]);
  const invalidCount = Object.keys(cellErrors).length;

  // Per-row error slice, keyed by row __rid — lets each CsvRow memo its
  // own error props and skip re-renders when a different row's cells change.
  // Walks cellErrors (bounded by error count) instead of rows × cols — valid
  // tables skip the loop entirely.
  const errorsByRid = useMemo(() => {
    const out: Record<string, Record<string, string>> = {};
    for (const [k, err] of Object.entries(cellErrors)) {
      const sep = k.indexOf(":");
      if (sep === -1) continue;
      const i = Number(k.slice(0, sep));
      const colKey = k.slice(sep + 1);
      const rid = rows[i]?.__rid;
      if (!rid) continue;
      (out[rid] ??= {})[colKey] = err;
    }
    return out;
  }, [rows, cellErrors]);
  const gridTemplateColumns = useMemo(() => `${columns.map(() => "1fr").join(" ")} 36px`, [columns.length] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const visibleRows = isTruncated ? rows.slice(0, DISPLAY_MAX_ROWS) : rows;
  const hiddenCount = rows.length - visibleRows.length;

  // Summary footer: computed once per (rows, columns, summary, ctx) change.
  // Null when summary is disabled or has no aggregates — we render nothing
  // in that case. An empty array means declared but nothing resolved yet
  // (e.g. token metadata still loading) — we show a subtle "Computing..." line.
  const summaryEntries = useMemo(() => computeSummary(columnarFromRows, columns, summary, ctx, cellErrors), [columnarFromRows, columns, summary, ctx, cellErrors]);

  // Errors can live on hidden rows — submit gate would block without the user
  // being able to locate them. Surface the count so they know to clear.
  const hiddenErrorCount = useMemo(() => {
    if (!isTruncated) return 0;
    let count = 0;
    for (const k of Object.keys(cellErrors)) {
      const sep = k.indexOf(":");
      if (sep === -1) continue;
      const i = Number(k.slice(0, sep));
      if (i >= DISPLAY_MAX_ROWS) count++;
    }
    return count;
  }, [isTruncated, cellErrors]);
  return <div className="space-y-2">
      {isTruncated ? <div className="flex flex-wrap items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-600/60 dark:bg-amber-900/30 dark:text-amber-200">
          <IconAlertWarningOutline18 className="h-3.5 w-3.5 flex-shrink-0" />
          <span className="flex-1">
            Showing first {DISPLAY_MAX_ROWS.toLocaleString()} of {rows.length.toLocaleString()} rows. Large tables are
            truncated to keep the editor responsive — your full data is saved and will be used by the workflow.
            {hiddenErrorCount > 0 ? <>
                {" "}
                <span className="font-semibold">
                  {hiddenErrorCount} invalid {hiddenErrorCount === 1 ? "cell is" : "cells are"} in hidden rows
                </span>{" "}
                — clear hidden rows or narrow the paste to edit them.
              </> : null}
          </span>
          <Button type="button" variant="ghost" size="sm" onClick={clearHiddenRows}>
            Clear hidden rows
          </Button>
        </div> : null}

      <div className="overflow-hidden rounded-lg border border-zinc-200 dark:border-zinc-700">
        <div className="grid gap-2 border-b border-zinc-200 bg-zinc-50 px-3 py-2 dark:border-zinc-700 dark:bg-zinc-900/50" style={{
        gridTemplateColumns
      }}>
          {columns.map(c => <div key={c.key} className="label-xs-medium flex items-center gap-2 text-zinc-600 dark:text-zinc-400">
              <span>
                {c.name}
                {c.required ? <span className="ml-0.5 text-red-500">*</span> : null}
              </span>
            </div>)}
          <div />
        </div>

        <div className="divide-y divide-zinc-100 dark:divide-zinc-800">
          {visibleRows.map(row => <CsvRow key={row.__rid} rid={row.__rid} cells={row.cells} columns={columns} specs={specs} ctx={ctx} errors={errorsByRid[row.__rid]} canRemove={canRemove} gridTemplateColumns={gridTemplateColumns} onCellChange={setCell} onRemove={removeRow} />)}
        </div>
      </div>

      {/* Summary footer (aggregates declared in prop.properties.summary) */}
      {summaryEntries ? <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md bg-zinc-50 px-3 py-2 text-xs text-zinc-600 dark:bg-zinc-900/30 dark:text-zinc-400">
          {summaryEntries.length === 0 ? <span className="italic text-zinc-400 dark:text-zinc-500">Computing summary…</span> : summaryEntries.map((entry, i) => <span key={`${entry.label}-${i}`}>
                <strong className="font-medium">{entry.label}:</strong> {entry.formatted}
              </span>)}
        </div> : null}

      {/* Footer actions */}
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="white" size="sm" onClick={addRow} disabled={!canAdd}>
          <IconPlusOutline18 className="mr-1 h-3.5 w-3.5" />
          Add row
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={() => setPasteOpen(v => !v)}>
          <IconClipboardArrowInOutline18 className="mr-1 h-3.5 w-3.5" />
          Paste CSV
        </Button>
        <RowCounter rows={rows.length} maxRows={maxRows} hiddenCount={hiddenCount} />
        {invalidCount > 0 ? <span className="label-xs inline-flex items-center gap-1 text-red-500">
            <IconAlertWarningOutline18 className="h-3 w-3" />
            {invalidCount} invalid {invalidCount === 1 ? "cell" : "cells"}
          </span> : null}
      </div>

      {pasteNotice ? <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-600/60 dark:bg-amber-900/30 dark:text-amber-200">
          <IconAlertWarningOutline18 className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
          <span className="flex-1">{pasteNotice}</span>
          <button type="button" onClick={() => setPasteNotice(null)} className="label-xs text-amber-900/70 hover:text-amber-900 dark:text-amber-200/70 dark:hover:text-amber-200">
            Dismiss
          </button>
        </div> : null}

      {/* Paste CSV surface */}
      {pasteOpen ? <div className="space-y-2 rounded-lg border border-zinc-200 bg-zinc-50 p-3 dark:border-zinc-700 dark:bg-zinc-900/50">
          <p className="label-xs text-zinc-600 dark:text-zinc-400">
            One row per line. Columns separated by comma or tab, in order:{" "}
            <span className="font-mono">{columns.map(c => c.name).join(", ")}</span>. Amounts are in human units (e.g.{" "}
            <span className="font-mono">1.5</span> for 1.5 USDC). Quoted cells and embedded commas are not supported —
            use tabs if your data contains commas. Existing rows will be replaced.
          </p>
          <textarea value={pasteText} onChange={e => setPasteText(e.target.value)} rows={5} maxLength={PASTE_MAX_CHARS} className="w-full rounded-md border border-zinc-200 bg-white p-2 font-mono text-xs text-zinc-900 dark:border-zinc-700 dark:bg-zinc-950 dark:text-white" placeholder={`0xabc...,1.5\n0xdef...,2.25`} />
          <div className="flex gap-2">
            <Button type="button" size="sm" onClick={applyPaste} disabled={!pasteText.trim()}>
              Apply
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => {
          setPasteOpen(false);
          setPasteText("");
        }}>
              Cancel
            </Button>
          </div>
        </div> : null}
    </div>;
}
interface CsvRowProps {
  rid: string;
  cells: RowCells;
  columns: PropColumn[];
  specs: CellSpec[];
  ctx: CellContext;
  errors: Record<string, string> | undefined;
  canRemove: boolean;
  gridTemplateColumns: string;
  onCellChange: (rid: string, colKey: string, raw: string) => void;
  onRemove: (rid: string) => void;
}

/**
 * Single table row. React.memo-wrapped so editing one row doesn't re-render
 * siblings — the per-row `errors` slice from the parent is the only input
 * that changes per-row, plus `cells` for the edited row itself.
 */
const CsvRow = memo(function CsvRow(t0) {
  const $ = _c(31);
  const {
    rid,
    cells,
    columns,
    specs,
    ctx,
    errors,
    canRemove,
    gridTemplateColumns,
    onCellChange,
    onRemove
  } = t0;
  let t1;
  if ($[0] !== gridTemplateColumns) {
    t1 = {
      gridTemplateColumns
    };
    $[0] = gridTemplateColumns;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== cells || $[3] !== columns || $[4] !== ctx || $[5] !== errors || $[6] !== onCellChange || $[7] !== rid || $[8] !== specs) {
    let t3;
    if ($[10] !== cells || $[11] !== ctx || $[12] !== errors || $[13] !== onCellChange || $[14] !== rid || $[15] !== specs) {
      t3 = (col, ci) => {
        const Render = specs[ci].render;
        const raw = cells[col.key] == null ? "" : String(cells[col.key]);
        const err = errors?.[col.key];
        return <div key={col.key} className="flex flex-col"><Render column={col} value={raw} onChange={v => onCellChange(rid, col.key, v)} ctx={ctx} invalid={!!err} />{err ? <span className="mt-1 text-xs text-red-500">{err}</span> : null}</div>;
      };
      $[10] = cells;
      $[11] = ctx;
      $[12] = errors;
      $[13] = onCellChange;
      $[14] = rid;
      $[15] = specs;
      $[16] = t3;
    } else {
      t3 = $[16];
    }
    t2 = columns.map(t3);
    $[2] = cells;
    $[3] = columns;
    $[4] = ctx;
    $[5] = errors;
    $[6] = onCellChange;
    $[7] = rid;
    $[8] = specs;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[17] !== onRemove || $[18] !== rid) {
    t3 = () => onRemove(rid);
    $[17] = onRemove;
    $[18] = rid;
    $[19] = t3;
  } else {
    t3 = $[19];
  }
  const t4 = !canRemove;
  const t5 = canRemove ? "hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20" : "cursor-not-allowed opacity-40";
  let t6;
  if ($[20] !== t5) {
    t6 = cn("flex h-9 w-9 items-center justify-center rounded-md text-zinc-500 transition-colors", t5);
    $[20] = t5;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <IconTrash2Outline18 className="h-4 w-4" />;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== t3 || $[24] !== t4 || $[25] !== t6) {
    t8 = <button type="button" onClick={t3} disabled={t4} aria-label="Remove row" className={t6}>{t7}</button>;
    $[23] = t3;
    $[24] = t4;
    $[25] = t6;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== t1 || $[28] !== t2 || $[29] !== t8) {
    t9 = <div className="grid items-start gap-2 px-3 py-2" style={t1}>{t2}{t8}</div>;
    $[27] = t1;
    $[28] = t2;
    $[29] = t8;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  return t9;
});

/**
 * Row counter with 3-tier threshold:
 *   <80% of max  → neutral "<N> rows"
 *   80%–<100%    → amber "<N> of <MAX> rows"
 *   >=100%       → amber bold "<MAX> / <MAX> · limit reached"
 *
 * Hidden-count suffix appends in all tiers. Info tooltip renders when maxRows
 * is set so users know the cap is a template-level safeguard, not a bug.
 */
interface RowCounterProps {
  rows: number;
  maxRows: number | undefined;
  hiddenCount: number;
}
const ROW_COUNTER_TOOLTIP = "Templates can cap the number of rows for safety and display performance. For larger datasets, use Paste CSV or split into multiple workflows.";
function RowCounter(t0) {
  const $ = _c(8);
  const {
    rows,
    maxRows,
    hiddenCount
  } = t0;
  const hiddenSuffix = hiddenCount > 0 ? ` (${hiddenCount.toLocaleString()} hidden)` : "";
  let text;
  let tone;
  if (!maxRows || rows < 0.8 * maxRows) {
    text = `${rows} rows${hiddenSuffix}`;
    tone = "neutral";
  } else {
    if (rows < maxRows) {
      text = `${rows} of ${maxRows} rows${hiddenSuffix}`;
      tone = "amber";
    } else {
      text = `${maxRows} / ${maxRows} · limit reached${hiddenSuffix}`;
      tone = "amber-bold";
    }
  }
  const toneClass = tone === "neutral" ? "text-zinc-500" : tone === "amber" ? "text-amber-600 dark:text-amber-400" : "text-amber-600 font-medium dark:text-amber-400";
  let t1;
  if ($[0] !== toneClass) {
    t1 = cn("label-xs inline-flex items-center gap-1", toneClass);
    $[0] = toneClass;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== maxRows) {
    t2 = maxRows ? <TooltipProvider delayDuration={200}><Tooltip><TooltipTrigger asChild={true}><button type="button" aria-label="About the row limit" className="inline-flex h-3.5 w-3.5 items-center justify-center text-zinc-400 hover:text-zinc-600 dark:text-zinc-500 dark:hover:text-zinc-300"><IconAlertInfoOutline18 className="h-3 w-3" /></button></TooltipTrigger><TooltipContent className="max-w-xs">{ROW_COUNTER_TOOLTIP}</TooltipContent></Tooltip></TooltipProvider> : null;
    $[2] = maxRows;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2 || $[6] !== text) {
    t3 = <span className={t1}>{text}{t2}</span>;
    $[4] = t1;
    $[5] = t2;
    $[6] = text;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
interface SummaryEntry {
  label: string;
  formatted: string;
}
function defaultLabel(col: PropColumn, reduce: string): string {
  if (reduce === "sum") return `Total ${col.name.toLowerCase()}`;
  if (reduce === "count") return `${col.name} count`;
  return col.name;
}

/**
 * Compute rendered summary entries from the current columnar value + summary
 * config. Returns `null` when the summary is disabled (position !== "footer"
 * or no aggregates declared), an empty array when declared aggregates exist
 * but none could be computed (e.g. token metadata pending), and a populated
 * array otherwise.
 */
function computeSummary(columnar: CsvTableValue, columns: PropColumn[], summary: PropSchemaSummary | undefined, ctx: CellContext, cellErrors: Record<string, string>): SummaryEntry[] | null {
  if (!summary) return null;
  const position = summary.position ?? "footer";
  if (position !== "footer") return null;
  const aggregates = summary.aggregates ?? [];
  if (aggregates.length === 0) return null;

  // Rows where THIS column had a validation error must be excluded from the
  // aggregate — we already pre-filter empties below, but a bad format needs
  // explicit exclusion so bogus input can't skew the sum/count.
  const invalidRowsByColKey = new Map<string, Set<number>>();
  for (const k of Object.keys(cellErrors)) {
    const sep = k.indexOf(":");
    if (sep === -1) continue;
    const i = Number(k.slice(0, sep));
    const colKey = k.slice(sep + 1);
    if (!Number.isFinite(i)) continue;
    const set = invalidRowsByColKey.get(colKey) ?? new Set<number>();
    set.add(i);
    invalidRowsByColKey.set(colKey, set);
  }
  const entries: SummaryEntry[] = [];
  for (const agg of aggregates) {
    const col = columns.find(c => c.key === agg.column);
    if (!col) continue;
    const spec = getCellSpec(col);
    // Explicit allow-list instead of `spec.aggregate?.[agg.reduce as ...]`.
    // `agg.reduce` is typed `"sum" | "count" | string` for forward-compat, but
    // a raw bracket access would let `"constructor"` / `"toString"` reach
    // `Object.prototype` methods and crash when called with reducer args.
    // Adding a new reducer = one branch here + one entry in the cell spec.
    const reduce = agg.reduce;
    const reducer = reduce === "sum" ? spec.aggregate?.sum : reduce === "count" ? spec.aggregate?.count : undefined;
    if (!reducer) continue;
    const raw = columnar[col.key] ?? [];
    const invalidRows = invalidRowsByColKey.get(col.key);
    const values: string[] = [];
    for (let i = 0; i < raw.length; i++) {
      const v = raw[i];
      const s = v == null ? "" : String(v);
      if (s === "") continue;
      if (invalidRows?.has(i)) continue;
      values.push(s);
    }
    const result = reducer(values, col, ctx);
    if (!result) continue;
    entries.push({
      label: agg.label ?? defaultLabel(col, agg.reduce),
      formatted: result.formatted
    });
  }
  return entries;
}